// Note: a coloured card you write Markdown into. It shows the text rendered; clicking it (or Enter once it has
// focus) swaps in the plain text to edit, and leaving the field renders it again. Links in it open in a new tab.
// The strip along its top is where you drag it from; the text saves itself half a second after typing stops (and
// when the note loses focus). Viewers of a locked webtop can read it only; they see the same strip, but without
// the grab cursor or tooltip, since nothing on a locked webtop moves. Its colour is the tile's own `color` field
// (picked with the shared colour menu), not part of its data.
import { colorMenu, currentRole, EDIT, editField, isLight, lineIcon, patchTile, saveData, tileNode } from './kit.js';
import { markdown, TASK_LINE } from './markdown.js';

export default {
  kind: 'note',
  label: 'Note',
  icon: lineIcon('note'),
  about: 'Markdown text and checklists, edited in place',
  look: 'card',
  sizes: ['2x2', '2x1', '3x1', '3x2'],
  fields: '.note-view',
  create: () => ({ title: 'Note', color: null, data: { text: '' } }),
  render: renderNote,
  menu: (s) => [{ label: 'Background Color', sub: true, roles: EDIT, action: () => colorMenu(noteColor(s)) }],
};

const NOTE_SAVE_MS = 500;

function renderNote(s, el) {
  el.setAttribute('role', 'group');
  el.setAttribute('aria-label', 'Note');
  paintNote(el, s.color);
  const editor = currentRole() === 'editor';
  const view = document.createElement('div');
  view.className = 'note-view';
  const show = (text) => {
    view.innerHTML = markdown(text);
    if (editor) view.querySelectorAll('input[data-line]').forEach((box) => { box.disabled = false; box.title = 'Tick to update the note'; });
  };
  show(s.data?.text || '');
  const ta = document.createElement('textarea');
  ta.className = 'note-text';
  ta.value = s.data?.text || '';
  ta.placeholder = 'Write a note';
  ta.maxLength = 5000;
  ta.readOnly = !editor;
  ta.setAttribute('aria-label', 'Note text');
  const flush = () => { if (ta.dirty) { ta.dirty = false; clearTimeout(ta.timer); saveData(s, { text: ta.value }); } };
  ta.addEventListener('input', () => { ta.dirty = true; clearTimeout(ta.timer); ta.timer = setTimeout(flush, NOTE_SAVE_MS); });
  ta.addEventListener('blur', () => {
    flush();
    show(ta.value);
    el.classList.remove('editing');
  });
  if (editor) {
    view.tabIndex = 0;
    view.title = 'Click to edit';
    const edit = () => { editField(ta); ta.setSelectionRange(ta.value.length, ta.value.length); };
    // A ticked task box writes "[X]" (or "[ ]") into its line of the text and saves it.
    const tick = (box) => {
      const lines = ta.value.split('\n');
      const n = Number(box.dataset.line);
      lines[n] = lines[n].replace(TASK_LINE, `$1[${box.checked ? 'X' : ' '}]`);
      ta.value = lines.join('\n');
      saveData(s, { text: ta.value });
      show(ta.value);
    };
    view.addEventListener('click', (e) => {
      if (e.target.matches('input[data-line]')) return tick(e.target);
      if (!e.target.closest('a')) edit();
    });
    view.addEventListener('keydown', (e) => { if (e.key === 'Enter' && e.target === view) { e.preventDefault(); edit(); } });
  }
  const grip = document.createElement('div');
  grip.className = editor ? 'note-grip movable' : 'note-grip';
  if (editor) grip.title = 'Drag to move';
  el.append(grip, view, ta);
}

function paintNote(el, color) {
  el.style.setProperty('--note-color', color || 'var(--note)');
  el.classList.toggle('light', !!color && isLight(color));
}
// A note's colour as a colour target for the shared colour menu.
function noteColor(s) {
  return {
    title: 'Note Color',
    get: () => s.color,
    fallback: () => getComputedStyle(document.documentElement).getPropertyValue('--note').trim().toLowerCase(),
    preview(c) { const el = tileNode(s); if (el) paintNote(el, c); },
    async save(c) {
      s.color = c;
      await patchTile(s, { color: c });
      const el = tileNode(s);
      if (el) paintNote(el, c);
    },
  };
}
